import { Component, lazy, Suspense, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import {
  BadgeCheck,
  CalendarDays,
  ChevronDown,
  Clock,
  GraduationCap,
  ICON_STROKE_WIDTH,
  Icon,
  MapPin,
  Ticket,
} from './components/Icon';
import panelDiscussionImage from '../ai-panel-discussion.webp';
import venueImage from '../Victor-E-Bruce-Complex.jpg';
import galleryCraigImg from '../Dr.-Craig-Ramlal-2-.png';
import galleryPanelImg from '../1757889897233.jpg';
import galleryStudioImg from '../1737557750179.jpg';
import galleryAtmosphereImg from '../Gemini_Generated_Image_ebqi0qebqi0qebqi.png';
import { assets, audienceGroups, eventFacts, faqs, outcomes, programme, speakers } from './content';
import { RollingButton } from './components/RollingButton';
import { SiteNav } from './components/SiteNav';
import { FeatureSteps } from './components/ui/feature-section';
import BentoGallery from './components/ui/bento-gallery';

const RSVP_FORM_SRC = 'https://form.jotform.com/262144246508051';

const HeroShader = lazy(() => import('./components/HeroShader'));

class ShaderBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(_error: Error, _info: ErrorInfo) { /* Static fallback remains visible. */ }
  render() { return this.state.failed ? null : this.props.children; }
}

function useHeroShader() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: no-preference)');
    const sync = () => setEnabled(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return enabled;
}

function RegistrationClosedBanner() {
  return (
    <div className="relative z-40 bg-[var(--fg)] text-white" data-od-id="registration-closed-banner">
      <div className="mx-auto flex max-w-[1440px] items-center justify-center gap-2.5 px-5 py-2.5 text-center sm:px-8">
        <Icon name="closed" size="sm" decorative={false} aria-label="Registrations closed" />
        <p className="text-[13px] font-semibold tracking-[-0.01em] sm:text-sm">
          Online RSVPs are now closed — registrations for AI Ready Tobago are no longer being accepted.
        </p>
      </div>
    </div>
  );
}

function useHashRoute() {
  const [hash, setHash] = useState(() => (typeof window === 'undefined' ? '' : window.location.hash));

  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return hash;
}

function RegistrationClosedPage() {
  return (
    <div className="flex min-h-[100svh] flex-col bg-[var(--bg)]" data-od-id="registration-closed-page">
      <header className="mx-auto w-full max-w-[1440px] p-2 sm:p-3">
        <nav className="flex min-h-14 items-center rounded-full bg-white p-[5px] shadow-[0_10px_35px_rgba(17,24,39,.08)]" aria-label="Registration closed">
          <a href="#top" className="flex h-11 items-center rounded-full px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" aria-label="AI Ready Tobago home">
            <img src={assets.logo} alt="Tobago Information Technology Limited" className="h-10 w-auto max-w-[200px] object-contain sm:h-11" />
          </a>
        </nav>
      </header>

      <main className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col items-center justify-center px-5 py-16 text-center sm:px-8">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-white shadow-[0_12px_35px_rgba(17,24,39,.12)]">
          <Icon name="closed" size="xl" className="text-[var(--accent)]" decorative={false} aria-label="Registrations closed" />
        </div>

        <h1 data-od-id="closed-heading" className="mt-8 max-w-[16ch] text-[clamp(2.6rem,7vw,5rem)] font-medium leading-[.95] tracking-[-0.055em] text-[var(--fg)]">
          Registrations are now closed.
        </h1>

        <p className="mt-6 max-w-[54ch] text-[15px] font-medium leading-7 text-[color-mix(in_oklch,var(--fg)_72%,var(--muted))] sm:text-base">
          Online RSVPs for AI Ready Tobago are no longer being accepted. Thank you to everyone who reserved a seat — we look forward to welcoming you on Thursday, 13 August 2026 at the Victor E. Bruce Financial Complex, Scarborough.
        </p>

        <dl className="mt-10 grid w-full max-w-[680px] grid-cols-1 gap-3 sm:grid-cols-3">
          {eventFacts.slice(0, 3).map((fact) => (
            <div key={fact.label} className="rounded-2xl border border-[var(--border)] bg-white px-5 py-4 text-left shadow-[0_8px_25px_rgba(17,24,39,.06)]">
              <dt className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)]">
                <fact.Icon className="h-3 w-3 text-[var(--accent)]" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> {fact.label}
              </dt>
              <dd className="mt-1.5 text-[13px] font-semibold leading-5 text-[var(--fg)] sm:text-sm">{fact.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row">
          <a
            href="#top"
            data-od-id="closed-back-button"
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-[var(--fg)] px-6 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
          >
            Back to the event page
          </a>
          <a
            href="#programme"
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-white px-6 text-[13px] font-semibold text-[var(--fg)] shadow-[0_8px_25px_rgba(17,24,39,.08)] transition-colors duration-300 hover:text-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
          >
            Explore the programme
          </a>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function SectionMarker({ number, label }: { number: string; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-7 w-7 place-items-center rounded-full bg-[var(--fg)] text-xs font-semibold text-white">{number}</span>
      <span className="rounded-full border border-[var(--border)] bg-white px-4 py-1.5 text-xs font-semibold text-[var(--fg)]">{label}</span>
    </div>
  );
}

function Hero({ onReserve }: { onReserve: () => void }) {
  const showShader = useHeroShader();

  return (
    <>
    <section id="top" className="relative flex min-h-[100svh] flex-col bg-[var(--bg)] lg:overflow-hidden" data-od-id="hero-section">
      {showShader ? <div className="pointer-events-none absolute inset-0 z-0 opacity-75" aria-hidden="true">
        <ShaderBoundary><Suspense fallback={null}><HeroShader /></Suspense></ShaderBoundary>
      </div> : null}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(90deg,var(--bg)_0%,color-mix(in_oklch,var(--bg)_92%,transparent)_46%,transparent_72%)]" aria-hidden="true" />
      <SiteNav onReserve={onReserve} />

      <div className="relative z-20 mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-end px-5 pb-12 pt-8 sm:px-8 sm:pb-10 lg:px-12 lg:pb-12">
        <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(380px,.85fr)] lg:gap-10">
          <div className="max-w-4xl pb-2 lg:pb-8">
            <p className="mb-5 text-[13px] font-semibold tracking-[0.08em] text-[var(--fg)] sm:mb-7">Tobago Information Technology Limited presents</p>
            <h1 data-od-id="hero-heading" className="max-w-[12ch] text-[clamp(3.3rem,12vw,5.4rem)] font-medium leading-[.92] tracking-[-0.065em] text-[var(--fg)] sm:text-[clamp(4rem,8vw,6.6rem)] lg:text-[clamp(5rem,7vw,7.5rem)]">
              AI Ready<br />Tobago
            </h1>
            <h2 className="mt-5 max-w-[24ch] text-[clamp(1.25rem,3vw,2rem)] font-medium leading-[1.14] tracking-[-0.03em] text-[var(--fg)]">Preparing Tobago for an AI-Driven Future.</h2>
            <p className="mt-5 max-w-[58ch] text-[15px] font-medium leading-7 text-[color-mix(in_oklch,var(--fg)_74%,var(--muted))] sm:text-base">
              The Future Belongs to Those Who Embrace Innovation.<br /><br />Artificial Intelligence is redefining how nations compete, businesses grow, governments deliver services, and people work, learn, and innovate.<br /><br />Be part of Tobago&rsquo;s most important conversation on AI as distinguished speakers share practical insights, real-world applications, and strategic perspectives on preparing Tobago to thrive in the age of Artificial Intelligence.<br /><br />Inspire Innovation. Empower People. Transform Tobago.
            </p>
            <div className="mt-7 flex flex-col items-start gap-3 sm:mt-9 sm:flex-row sm:items-center sm:gap-4">
              <RollingButton onClick={onReserve} dataOdId="hero-reserve-button">Reserve your free seat</RollingButton>
              <div className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 text-[13px] font-semibold text-[var(--fg)] shadow-[0_8px_25px_rgba(17,24,39,.08)]">
                <Ticket className="h-3.5 w-3.5 text-[var(--accent)]" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> Free · Limited RSVP
              </div>
            </div>
          </div>

          <div className="mx-auto grid h-[58svh] min-h-[460px] w-full max-w-[590px] grid-cols-[1.15fr_.85fr] gap-3 lg:h-[68svh] lg:max-h-[735px] lg:min-h-[560px]" data-od-id="hero-speaker-gallery">
            <div className="relative min-w-0 overflow-hidden rounded-[28px] bg-[var(--fg)] shadow-[0_24px_70px_rgba(17,24,39,.22)]" data-od-id="hero-joshua-portrait">
              <img src={assets.joshuaImage} alt="Featured speaker Joshua Powder" className="h-full w-full object-cover" style={{ objectPosition: 'center 18%' }} width="996" height="1495" fetchPriority="high" />
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/25 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-3 left-3 right-3 rounded-2xl bg-white/[.94] p-3 shadow-[0_12px_35px_rgba(0,0,0,.25)] backdrop-blur-md sm:bottom-4 sm:left-4 sm:right-4 sm:p-4">
                <p className="mt-1.5 text-base font-semibold tracking-[-0.025em] text-[var(--fg)] sm:text-lg">Joshua Powder</p>
              </div>
            </div>
            <div className="grid min-w-0 grid-rows-2 gap-3">
              <div className="relative overflow-hidden rounded-[22px] bg-[var(--fg)] shadow-[0_16px_40px_rgba(17,24,39,.16)]" data-od-id="hero-keron-rose-portrait">
                <img src={assets.keronImage} alt="Confirmed speaker Keron Rose" className="h-full w-full object-cover" style={{ objectPosition: 'center 18%' }} width="996" height="1495" loading="eager" />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" aria-hidden="true" />
                <p className="absolute bottom-3 left-3 right-3 text-sm font-semibold tracking-[-0.02em] text-white sm:bottom-4 sm:left-4">Keron Rose</p>
              </div>
<div className="relative overflow-hidden rounded-[22px] bg-[var(--fg)] shadow-[0_16px_40px_rgba(17,24,39,.16)]" data-od-id="hero-craig-ramlal-portrait">
              <img src={assets.craigImage} alt="Confirmed speaker Dr Craig Ramlal" className="h-full w-full object-cover" style={{ objectPosition: 'center 30%' }} width="996" height="1495" loading="eager" />
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 to-transparent" aria-hidden="true" />
              <p className="absolute bottom-4 left-4 right-4 text-base font-semibold tracking-[-0.02em] text-white sm:bottom-5 sm:left-5">Dr Craig Ramlal</p>
            </div>
            </div>
          </div>
        </div>
      </div>
      </section>
      <div className="relative z-20 flow-root bg-[var(--bg)] px-5 pb-8 sm:px-8 sm:pb-10 lg:px-12 lg:pb-12">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 overflow-hidden rounded-2xl border border-white/70 bg-white/88 shadow-[0_14px_40px_rgba(17,24,39,.08)] backdrop-blur-xl lg:grid-cols-4" data-od-id="event-fact-strip">
          {eventFacts.map((fact, index) => (
            <div key={fact.label} className={`min-w-0 px-4 py-4 sm:px-5 ${index % 2 ? 'border-l border-[var(--border)]' : ''} ${index > 1 ? 'border-t border-[var(--border)] lg:border-t-0' : ''} ${index === 2 ? 'lg:border-l' : ''}`}>
              <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)]">
                <fact.Icon className="h-3 w-3 text-[var(--accent)]" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" />
                {fact.label}
              </p>
              <p className="mt-1.5 text-[13px] font-semibold leading-5 text-[var(--fg)] sm:text-sm">{fact.value}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function WhyAttend({ onReserve }: { onReserve: () => void }) {
  return (
    <section id="why-attend" className="overflow-hidden bg-white py-20 sm:py-24 lg:py-32" data-od-id="why-attend-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <SectionMarker number="01" label="Why AI Ready Tobago" />
        <div className="mt-7 grid gap-8 lg:grid-cols-[minmax(0,1.35fr)_minmax(300px,.65fr)] lg:items-end">
          <h2 data-od-id="why-attend-heading" className="max-w-[20ch] text-[clamp(2.4rem,6vw,5.25rem)] font-medium leading-[.98] tracking-[-0.055em] text-[var(--fg)]">Build practical AI confidence for what comes next.</h2>
          <div className="max-w-lg lg:justify-self-end">
            <p className="text-[15px] font-medium leading-7 text-[color-mix(in_oklch,var(--fg)_72%,var(--muted))] sm:text-base">Connect the global AI conversation to Tobago’s real opportunities and responsibilities—whether you are opening an AI tool for the first time or building a responsible approach at work.</p>
            <RollingButton onClick={onReserve} className="mt-6" dataOdId="why-reserve-button">Reserve your free seat</RollingButton>
          </div>
        </div>

        <div className="mt-16 border-t border-[var(--border)] lg:mt-24">
          {outcomes.map(([number, title, description]) => (
            <div key={number} className="grid gap-3 border-b border-[var(--border)] py-6 sm:grid-cols-[56px_minmax(180px,.7fr)_1.3fr] sm:items-start sm:gap-5 lg:py-8">
              <span className="font-mono text-xs font-semibold text-[var(--accent)]">{number}</span>
              <h3 className="text-lg font-semibold tracking-[-0.02em] text-[var(--fg)] sm:text-xl">{title}</h3>
              <p className="max-w-2xl text-sm leading-6 text-[var(--muted)] sm:text-[15px]">{description}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 grid gap-5 lg:mt-28 lg:grid-cols-[.78fr_1.22fr]">
          <div className="relative min-h-[430px] overflow-hidden rounded-[24px] bg-[var(--fg)] sm:min-h-[560px]" data-od-id="supporting-speaker-image">
            <img src={panelDiscussionImage} alt="AI symposium panel discussion" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: 'center 18%' }} width="1024" height="1536" loading="eager" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" aria-hidden="true" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/70">Caribbean perspective</p>
              <p className="mt-2 text-2xl font-medium tracking-[-0.035em]">Responsible AI needs regional voices.</p>
            </div>
          </div>

          <div id="programme" className="rounded-[24px] bg-[var(--bg)] p-6 sm:p-9 lg:p-12" data-od-id="programme-preview">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--accent)]">Programme preview</p>
            <h3 className="mt-4 max-w-[18ch] text-[clamp(2rem,4vw,3.7rem)] font-medium leading-[1.02] tracking-[-0.045em] text-[var(--fg)]">An evening built for understanding and action.</h3>
            <div className="mt-9">
              {programme.map(([title, description], index) => (
                <div key={title} className="grid grid-cols-[38px_1fr] gap-3 border-t border-[var(--border)] py-5 first:border-t-0 first:pt-0">
                  <span className="font-mono text-xs font-semibold text-[var(--accent)]">{String(index + 1).padStart(2, '0')}</span>
                  <div><h4 className="text-base font-semibold tracking-[-0.02em] text-[var(--fg)]">{title}</h4><p className="mt-1.5 text-sm leading-6 text-[var(--muted)]">{description}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 lg:mt-28">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--accent)]">Who should attend</p>
          <div className="mt-6 grid gap-8 border-y border-[var(--border)] py-8 md:grid-cols-3 md:divide-x md:divide-[var(--border)]">
            {audienceGroups.map((group, index) => (
              <div key={group.title} className={index ? 'md:pl-8' : ''}>
                <h3 className="text-xl font-semibold tracking-[-0.025em] text-[var(--fg)]">{group.title}</h3>
                <p className="mt-3 text-sm leading-6 text-[var(--muted)]">{group.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SpeakersAndAction({ onReserve }: { onReserve: () => void }) {
  const featured = speakers[0];
  const supporting = speakers.slice(1);
  const showLegacySpeakerCollage = false;
  return (
    <section id="speakers" className="bg-[var(--bg)] py-20 sm:py-24 lg:py-28" data-od-id="speakers-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <SectionMarker number="02" label="Featured speakers" />
        <h2 data-od-id="speakers-heading" className="mt-7 max-w-[18ch] text-[clamp(2.7rem,7vw,6.2rem)] font-medium leading-[.94] tracking-[-0.06em] text-[var(--fg)]">Meet the voices shaping an AI-ready Tobago.</h2>

        <FeatureSteps
          features={speakers.map((speaker) => ({
            step: speaker.topic,
            title: speaker.name,
            content: speaker.bio,
            image: speaker.image,
            imagePosition: speaker.imagePosition,
          }))}
          autoPlayInterval={5000}
          imageHeight="min-h-[430px] md:min-h-[540px] lg:min-h-[620px]"
        />

        {showLegacySpeakerCollage ? <article className="mt-12 grid overflow-hidden rounded-[28px] bg-[var(--fg)] text-white shadow-[0_28px_80px_rgba(17,24,39,.16)] lg:mt-16 lg:grid-cols-[1.05fr_.95fr]" data-od-id="speaker-card-joshua-powder">
          <div className="relative min-h-[470px] overflow-hidden lg:min-h-[680px]">
            <img src={featured.image} alt={featured.name} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: featured.imagePosition }} width="996" height="1495" loading="eager" />
          </div>
          <div className="flex flex-col justify-between p-6 sm:p-10 lg:p-14">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-white"><BadgeCheck className="h-3.5 w-3.5" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> Confirmed speaker</div>
              <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-white/55">Featured perspective</p>
              <h3 className="mt-3 text-[clamp(2.5rem,6vw,5rem)] font-medium leading-[.96] tracking-[-0.055em]">{featured.name}</h3>
              <p className="mt-5 text-xl font-medium leading-tight text-white/85 sm:text-2xl">{featured.topic}</p>
              <p className="mt-7 max-w-xl text-[15px] leading-7 text-white/65 sm:text-base">{featured.bio}</p>
            </div>
            <div className="mt-12 border-t border-white/15 pt-6 text-sm font-semibold text-white/65">Connectivity · Computing · Security · Resilience</div>
          </div>
        </article> : null}

        {showLegacySpeakerCollage ? <div className="mt-5 grid gap-5 md:grid-cols-2">
          {supporting.map((speaker) => (
            <article key={speaker.id} className="group overflow-hidden rounded-[24px] bg-white shadow-[0_16px_45px_rgba(17,24,39,.08)]" data-od-id={`speaker-card-${speaker.id}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--border)]">
                <img src={speaker.image} alt={speaker.name} className="h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)] motion-safe:group-hover:scale-[1.025]" style={{ objectPosition: speaker.imagePosition }} width="996" height="1495" loading="eager" />
                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-semibold text-[var(--fg)] shadow-[0_8px_24px_rgba(0,0,0,.18)]"><BadgeCheck className="h-3.5 w-3.5 text-[var(--accent)]" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> Confirmed speaker</div>
              </div>
              <div className="p-6 sm:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--accent)]">{speaker.topic}</p>
                <h3 className="mt-3 text-3xl font-medium tracking-[-0.045em] text-[var(--fg)] sm:text-4xl">{speaker.name}</h3>
                <p className="mt-5 text-sm leading-7 text-[var(--muted)] sm:text-[15px]">{speaker.bio}</p>
              </div>
            </article>
          ))}
        </div> : null}

        <div className="mt-20 overflow-hidden rounded-[28px] bg-[var(--accent)] text-white lg:mt-28" data-od-id="course-preview">
          <div className="grid gap-10 p-6 sm:p-10 lg:grid-cols-[1.15fr_.85fr] lg:items-end lg:p-14">
            <div>
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                <GraduationCap className="h-3.5 w-3.5" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> The learning continues
              </p>
              <h3 className="mt-4 max-w-[13ch] text-[clamp(2.5rem,6vw,5.4rem)] font-medium leading-[.96] tracking-[-0.055em]">Continue the learning with TITL.</h3>
            </div>
            <div className="lg:pb-2">
              <p className="text-xl font-semibold leading-tight">Applied AI Foundations: Work, Business & Public Service</p>
              <p className="mt-4 text-sm leading-7 text-white/75 sm:text-[15px]">A planned beginner-friendly pathway to help Tobago professionals apply AI responsibly to real work and business needs. Register interest for approved course updates.</p>
            </div>
          </div>
        </div>

        <div id="faq" className="mt-20 grid gap-10 lg:mt-28 lg:grid-cols-[.75fr_1.25fr] lg:gap-16" data-od-id="faq-region">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--accent)]">Plan your evening</p>
            <h3 className="mt-4 text-[clamp(2.3rem,5vw,4.5rem)] font-medium leading-[.98] tracking-[-0.05em] text-[var(--fg)]">Answers before you reserve.</h3>
            <div className="mt-8 rounded-2xl bg-white p-5 shadow-[0_14px_40px_rgba(17,24,39,.07)] sm:p-6" data-od-id="venue-card">
              <img src={venueImage} alt="Victor E. Bruce Financial Complex in Scarborough, Tobago" className="aspect-[16/9] w-full rounded-xl object-cover object-[58%_50%]" data-od-id="venue-photo" loading="lazy" />
              <MapPin className="mt-5 h-5 w-5 text-[var(--accent)]" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" />
              <p className="mt-4 text-lg font-semibold text-[var(--fg)]">Victor E. Bruce Financial Complex</p>
              <p className="mt-1 text-sm leading-6 text-[var(--muted)]">Penthouse, Post Office Rd., Scarborough, Tobago</p>
              <div className="mt-5 flex items-center gap-2 border-t border-[var(--border)] pt-5 text-xs font-semibold text-[var(--muted)]">
                <Clock className="h-5 w-5" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> 5:00–8:00 p.m.
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--border)]">
            {faqs.map(([question, answer], index) => (
              <details key={question} className="group border-b border-[var(--border)]" open={index === 0}>
                <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-4 rounded-lg py-5 text-base font-semibold text-[var(--fg)] marker:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 sm:text-lg">
                  {question}<ChevronDown className="h-5 w-5 shrink-0 text-[var(--muted)] transition-transform duration-300 group-open:rotate-180" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" />
                </summary>
                <p className="max-w-2xl pb-6 pr-8 text-sm leading-7 text-[var(--muted)] sm:text-[15px]">{answer}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="mt-20 border-t border-[var(--border)] pt-14 text-center lg:mt-28 lg:pt-20" data-od-id="final-cta">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-[var(--muted)] shadow-[0_8px_24px_rgba(17,24,39,.07)]">
            <CalendarDays className="h-4 w-4 text-[var(--accent)]" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> Thursday, 13 August 2026
          </div>
          <h3 className="mx-auto mt-6 max-w-[13ch] text-[clamp(3rem,8vw,7rem)] font-medium leading-[.92] tracking-[-0.065em] text-[var(--fg)]">Learn it. Apply it. Lead with it.</h3>
          <p className="mx-auto mt-6 max-w-lg text-[15px] leading-7 text-[var(--muted)]">Admission is free. Your RSVP helps TITL prepare the right venue, seating, and event experience.</p>
        </div>
      </div>
    </section>
  );
}

function RsvpSection() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
    script.async = true;
    script.onload = () => {
      const api = (window as unknown as { jotformEmbedHandler?: (sel: string, base: string) => void }).jotformEmbedHandler;
      api?.("iframe[id='JotFormIFrame-262144246508051']", 'https://form.jotform.com/');
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <section id="rsvp" aria-label="Reserve your free seat" data-od-id="rsvp-section" className="bg-[var(--bg)] py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <SectionMarker number="03" label="Reserve your seat" />
        <div className="mt-7 grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,.95fr)] lg:items-end">
          <h2 data-od-id="rsvp-heading" className="max-w-[18ch] text-[clamp(2.4rem,6vw,5rem)] font-medium leading-[.96] tracking-[-0.055em] text-[var(--fg)]">
            Reserve your free seat.
          </h2>
          <p className="max-w-lg text-[15px] font-medium leading-7 text-[color-mix(in_oklch,var(--fg)_72%,var(--muted))] sm:text-base">
            Free admission, limited capacity. Complete the TITL registration form to confirm your seat and help TITL prepare the right venue, seating, and event experience.
          </p>
        </div>

        <div className="mt-10 mx-auto w-full max-w-[700px]">
          <div className="w-full rounded-2xl border border-[var(--border)] bg-white shadow-[0_24px_70px_rgba(17,24,39,.12)]">
            <iframe
              id="JotFormIFrame-262144246508051"
              title="Reserve your free seat — AI Ready Tobago registration form"
              onLoad={() => window.parent.scrollTo(0, 0)}
              allowTransparency
              allow="geolocation; microphone; camera; fullscreen; payment"
              src={RSVP_FORM_SRC}
              width="640"
              height="539"
              frameBorder={0}
              scrolling="no"
              loading="lazy"
              className="block w-full"
            >Loading…</iframe>
          </div>
        </div>
      </div>
    </section>
  );
}

const galleryItems = [
  {
    id: 'governance',
    title: 'Caribbean governance',
    desc: 'Dr Craig Ramlal, governance perspective.',
    url: galleryCraigImg,
    span: 'md:col-span-2 md:row-span-2',
  },
  {
    id: 'panel',
    title: 'Voices in conversation',
    desc: 'Caribbean perspectives, side by side.',
    url: galleryPanelImg,
    span: 'md:row-span-1',
  },
  {
    id: 'studio',
    title: 'Behind the mic',
    desc: 'Working session, microphone live.',
    url: galleryStudioImg,
    span: 'md:row-span-1',
  },
  {
    id: 'atmosphere',
    title: 'At the lectern',
    desc: 'A regional moment worth showing up for.',
    url: galleryAtmosphereImg,
    span: 'md:col-span-2 md:row-span-1',
    position: 'top' as const,
  },
];

function GallerySection() {
  return (
    <section
      id="gallery"
      aria-label="Event moments"
      data-od-id="gallery-section"
      className="bg-white"
    >
      <BentoGallery
        imageItems={galleryItems}
        title="A Tobago worth showing up for."
        description="Caribbean voices, working moments, and the island that hosts them — drag the row to explore, click any tile to expand."
      />
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-white" data-od-id="site-footer">
      <div className="mx-auto border-t border-[var(--border)] px-5 py-6 text-center sm:px-8 lg:px-12">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--muted)] sm:text-[13px]" data-od-id="footer-closing-line">
          Thursday, 13 August 2026 · Post Office Rd., Scarborough, Tobago · Free admission
        </p>
      </div>
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-5 py-8 sm:px-8 md:flex-row md:items-end md:justify-between lg:px-12">
        <div><img src={assets.logo} alt="Tobago Information Technology Limited" className="h-11 w-auto max-w-[260px] object-contain" /><p className="mt-4 text-sm font-semibold text-[var(--fg)]">AI Ready Tobago: The TITL AI Symposium 2026</p></div>
        <div className="text-sm text-[var(--muted)] md:text-right">
          <p>
            <a href="/#privacy" className="underline decoration-[var(--border)] underline-offset-2 hover:text-[var(--fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2">
              Privacy policy
            </a>{' '}
            link to be confirmed before launch.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const hash = useHashRoute();
  const isClosed = hash === '#registration-closed';

  useEffect(() => {
    if (isClosed) {
      window.scrollTo(0, 0);
      return;
    }
    const id = hash.replace('#', '');
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, isClosed]);

  const openRegistration = () => {
    window.location.hash = 'registration-closed';
  };

  if (isClosed) return <RegistrationClosedPage />;

  return (
    <>
      <RegistrationClosedBanner />
      <main>
        <Hero onReserve={openRegistration} />
        <WhyAttend onReserve={openRegistration} />
        <SpeakersAndAction onReserve={openRegistration} />
        <GallerySection />
        <RsvpSection />
      </main>
      <SiteFooter />
    </>
  );
}
